import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { saveBlob } from '@nyce/admin-kit/api';
import { FormDialog } from '@nyce/admin-kit/console';
import { Button, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  SortSelect,
  SortTh,
  TableScroller,
  useAsync,
  useSort,
  type SortKeys,
  useInterval,
  useNotice,
} from '../../components/common';
import { formatBytes, formatDateTime, formatRelative } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import { useTargets } from '../../identity/session';
import {
  printQueue,
  type PrintJob,
  type Printer,
  type PrintServer,
  type PrintServerReleases,
  type PrintServerState,
  type PrintServerUpdatePolicy,
} from '../api/client';
import { usePrintQueueLinks } from '../links';
import {
  LOGS_MIN_VERSION,
  LogsStateChip,
  PrintServerStateChip,
  TenantCell,
  UpdateStateChip,
  VersionCell,
  canSendLogs,
  latestRelease,
  logsWaiting,
  releasesOrNull,
  pqPath,
  printServersOrNull,
  serverKey,
  tenantLabel,
  usePrintQueueTarget,
  usePrintQueueWrite,
} from '../shared';
import '../printqueue.css';

export interface ServerGroup {
  tenantId: string;
  name: string;
  /** Null when the print queue does not record print servers (before 1.5.0). */
  info: PrintServer | null;
  printers: Printer[];
}

const STATES: { value: '' | PrintServerState; label: string }[] = [
  { value: '', label: 'Any state' },
  { value: 'live', label: 'Live' },
  { value: 'polling', label: 'Polling' },
  { value: 'offline', label: 'Offline' },
];

const REFRESH_MS = 30_000;

/** Refresh now, and again every 30 s while auto is on (paused in a hidden tab); a refetch keeps the rows shown. */
function RefreshControls({
  auto,
  onAuto,
  loading,
  onRefresh,
}: {
  auto: boolean;
  onAuto: (on: boolean) => void;
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <>
      <Switch checked={auto} onChange={onAuto} label="Auto-refresh 30 s" />
      <Button variant="ghost" size="sm" icon={<RefreshCw size={14} />} loading={loading} onClick={onRefresh}>
        Refresh
      </Button>
    </>
  );
}

const STATE_RANK: Record<PrintServerState, number> = { live: 0, polling: 1, offline: 2 };

/** The print servers list's columns to sort by (within each tenant). */
export const SERVER_SORT: SortKeys<ServerGroup> = {
  name: { label: 'Print server', value: (g) => g.name },
  state: { label: 'State', value: (g) => (g.info ? STATE_RANK[g.info.state] : null) },
  printed: { label: 'Last printed', value: (g) => g.info?.lastPrintedAt, desc: true },
  printers: { label: 'Printers', value: (g) => g.printers.length, desc: true },
  version: { label: 'Version', value: (g) => g.info?.version },
};

/** Print servers from their contact rows and their printers, per tenant then name. */
export function serverGroups(printers: Printer[], servers: PrintServer[] | null): ServerGroup[] {
  const groups = new Map<string, ServerGroup>();
  const group = (tenantId: string, name: string) => {
    const k = serverKey(tenantId, name);
    let g = groups.get(k);
    if (!g) groups.set(k, (g = { tenantId, name, info: null, printers: [] }));
    return g;
  };
  for (const s of servers ?? []) group(s.tenantId, s.name).info = s;
  for (const p of printers) group(p.tenantId, p.printServerName).printers.push(p);
  for (const g of groups.values()) g.printers.sort((a, b) => a.name.localeCompare(b.name));
  return [...groups.values()].sort(
    (a, b) => a.tenantId.localeCompare(b.tenantId) || a.name.localeCompare(b.name)
  );
}

/** One print server's page (its printers); tenant and name in the query, like every other filter. */
export const printServerPath = (tenantId: string, name: string, target: string) =>
  pqPath(
    `/printqueue/printserver?tenant=${encodeURIComponent(tenantId)}&server=${encodeURIComponent(name)}`,
    target
  );

const jobsPath = (g: { tenantId: string; name: string }, target: string, status?: string) =>
  pqPath(
    `/printqueue/jobs?tenant=${encodeURIComponent(g.tenantId)}&server=${encodeURIComponent(g.name)}${status ? `&status=${status}` : ''}`,
    target
  );

interface Removed {
  notice: string;
  problems: string[];
}

function announce(toast: ReturnType<typeof useNotice>, message: string, problems: string[]) {
  if (problems.length) toast.error(`${message}. Not done: ${problems.join('; ')}`);
  else toast.success(message);
}

/** How many printed jobs the print server page shows (the print queue deletes them a day after printing). */
const RECENT_PRINTED = 10;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function JobLinks({ g, target }: { g: ServerGroup; target: string }) {
  if (!g.info) return <Link to={jobsPath(g, target)}>jobs</Link>;
  return (
    <span className="nyk-inline">
      <Link to={jobsPath(g, target, 'open')}>{g.info.openJobs} unfinished</Link>
      {g.info.errorJobs > 0 && (
        <Link className="nyk-text-red" to={jobsPath(g, target, 'Error')}>
          {g.info.errorJobs} failed
        </Link>
      )}
    </span>
  );
}

/**
 * Print servers grouped per tenant (organization) with their state; a print server opens its printers. A print server
 * replaces its printers with every upload (on start), so a removed print server that still runs registers again.
 */
export function PrintQueuePrintServersPage() {
  const target = usePrintQueueTarget();
  const api = useMemo(() => printQueue(target), [target]);
  const canWrite = usePrintQueueWrite();
  const links = usePrintQueueLinks(target);
  const [tenant, setTenant] = useParam('tenant', '');
  const [state, setState] = useParam<'' | PrintServerState>('state', '');
  const data = useAsync(async () => {
    const [printers, servers, releases] = await Promise.all([
      api.printers(),
      printServersOrNull(api),
      releasesOrNull(api),
    ]);
    return { printers, servers, releases };
  }, [api]);
  const [removing, setRemoving] = useState<ServerGroup | null>(null);
  const [updating, setUpdating] = useState<UpdateTarget | null>(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [auto, setAuto] = useState(true);
  useInterval(() => void data.reload(), REFRESH_MS, auto);
  const releases = data.data?.releases ?? null;
  const policy = useAsync(
    async () => (tenant && releases?.configured ? ((await api.updatePolicy(tenant))[0] ?? null) : null),
    [api, tenant, releases?.configured]
  );
  const latest = latestRelease(releases);
  const canUpdate = canWrite && !!releases?.configured;
  const toast = useNotice();
  const removed = useLocation().state as Removed | null;
  const navigate = useNavigate();
  useEffect(() => {
    // a print server removed on its own page lands here with the outcome
    if (!removed?.notice) return;
    announce(toast, removed.notice, removed.problems);
    navigate({ search: window.location.search }, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removed]);
  const tracked = !!data.data && data.data.servers !== null;
  const all = useMemo(
    () => (data.data ? serverGroups(data.data.printers, data.data.servers) : []),
    [data.data]
  );
  const tenants = [...new Set(all.map((g) => g.tenantId))].sort((a, b) => a.localeCompare(b));
  const shown = all.filter((g) => (!tenant || g.tenantId === tenant) && (!state || g.info?.state === state));
  const sort = useSort(shown, SERVER_SORT, useParam('sort', ''));
  const byTenant = [...new Set(shown.map((g) => g.tenantId))].map((t) => ({
    tenantId: t,
    servers: sort.rows.filter((g) => g.tenantId === t),
  }));
  const printerCount = shown.reduce((n, g) => n + g.printers.length, 0);
  const cols = canWrite ? 7 : 6;

  return (
    <Page
      title="Print servers"
      description="Print servers per tenant with their state. Open one for its printers; a print server reports its printers again when it starts and replaces the list each time."
      actions={
        <RefreshControls
          auto={auto}
          onAuto={setAuto}
          loading={data.loading && !!data.data}
          onRefresh={() => {
            void data.reload();
            void policy.reload();
          }}
        />
      }
    >
      {toast.element}
      <div className="pq-toolbar">
        <Select
          aria-label="Tenant"
          value={tenant}
          onChange={setTenant}
          options={[
            { value: '', label: `All tenants (${tenants.length})` },
            ...tenants.map((t) => ({ value: t, label: tenantLabel(t, links) })),
          ]}
        />
        {tracked && (
          <Select
            aria-label="State"
            value={state}
            onChange={(v) => setState(v as '' | PrintServerState)}
            options={STATES}
          />
        )}
        <SortSelect sort={sort} />
        {data.data && (
          <span className="nyk-ui-hint">
            {plural(shown.length, 'print server')} · {plural(printerCount, 'printer')}
          </span>
        )}
        {canUpdate && tenant && shown.some((g) => g.info?.updatable) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              setUpdating({
                tenantId: tenant,
                label: `every print server of ${tenantLabel(tenant, links)}`,
              })
            }
          >
            Update all…
          </Button>
        )}
        {canUpdate && tenant && (
          <Button size="sm" variant="ghost" onClick={() => setPolicyOpen(true)}>
            Update policy…
          </Button>
        )}
        {policy.data && (
          <span className="nyk-ui-hint" data-testid="pq-update-policy">
            {describePolicy(policy.data)}
          </span>
        )}
      </div>
      <ErrorBox error={data.error} />
      {data.data && !tracked && (
        <p className="nyk-ui-hint" data-testid="pq-untracked">
          This print queue is older than 1.5.0: it does not record when print servers were last in contact.
        </p>
      )}
      <div className="nyk-card">
        <Loading loading={data.loading && !data.data} />
        {!!shown.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="pq-printservers">
              <thead>
                <tr>
                  <SortTh sort={sort} by="name" />
                  <SortTh sort={sort} by="state" />
                  <SortTh sort={sort} by="printed" />
                  <SortTh sort={sort} by="printers" />
                  <th>Jobs</th>
                  <SortTh sort={sort} by="version" />
                  {canWrite && <th />}
                </tr>
              </thead>
              {byTenant.map((t) => (
                <tbody key={t.tenantId} className="nyk-row-group" data-tenant={t.tenantId}>
                  <tr className="pq-tenant-row">
                    <td colSpan={cols} className="nyk-card-title">
                      <div className="pq-server-head">
                        <TenantCell tenantId={t.tenantId} links={links} target={target} />
                        <span className="nyk-ui-hint">{plural(t.servers.length, 'print server')}</span>
                      </div>
                    </td>
                  </tr>
                  {t.servers.map((g) => (
                    <tr key={serverKey(g.tenantId, g.name)} data-server={g.name}>
                      <td data-label="Print server">
                        <Link to={printServerPath(g.tenantId, g.name, target)}>
                          <b>{g.name}</b>
                        </Link>
                      </td>
                      <td data-label="State">
                        {tracked ? (
                          <PrintServerStateChip server={g.info} />
                        ) : (
                          <span className="nyk-ui-hint">—</span>
                        )}
                      </td>
                      <td data-label="Last printed" className="nyk-nowrap">
                        <LastPrinted server={g.info} />
                      </td>
                      <td data-label="Printers" className="nyk-nowrap">
                        <Link to={printServerPath(g.tenantId, g.name, target)}>
                          {plural(g.printers.length, 'printer')}
                        </Link>
                      </td>
                      <td data-label="Jobs" className="nyk-nowrap">
                        <JobLinks g={g} target={target} />
                      </td>
                      <td data-label="Version">
                        <span className="nyk-inline">
                          <VersionCell server={g.info} latest={latest} />
                          <UpdateStateChip server={g.info} />
                          <LogsStateChip server={g.info} />
                        </span>
                      </td>
                      {canWrite && (
                        <td className="nyk-row-actions">
                          {canUpdate && g.info?.updatable && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setUpdating({
                                  tenantId: g.tenantId,
                                  name: g.name,
                                  label: g.name,
                                  version: g.info?.version,
                                })
                              }
                            >
                              Update
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setRemoving(g)}>
                            Remove
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </TableScroller>
        )}
        {data.data && !shown.length && (
          <EmptyState title={all.length ? 'No print server matches the filter.' : 'No print servers yet.'} />
        )}
      </div>
      {removing && (
        <RemoveDialog
          group={removing}
          tracked={tracked}
          target={target}
          onClose={() => setRemoving(null)}
          onRemoved={(message, problems) => {
            setRemoving(null);
            announce(toast, message, problems);
            void data.reload();
          }}
        />
      )}
      {policyOpen && releases && tenant && (
        <PolicyDialog
          tenantId={tenant}
          label={tenantLabel(tenant, links)}
          current={policy.data ?? null}
          releases={releases}
          target={target}
          onClose={() => setPolicyOpen(false)}
          onSaved={(saved) => {
            setPolicyOpen(false);
            toast.success(`Update policy of ${tenantLabel(tenant, links)}: ${describePolicy(saved)}`);
            void policy.reload();
          }}
        />
      )}
      {updating && releases && (
        <UpdateDialog
          update={updating}
          releases={releases}
          target={target}
          onClose={() => setUpdating(null)}
          onDone={(message, problems) => {
            setUpdating(null);
            announce(toast, message, problems);
            void data.reload();
          }}
        />
      )}
    </Page>
  );
}

/** One print server: its state, contacts and the printers it reported. */
export function PrintQueuePrintServerPage() {
  const target = usePrintQueueTarget();
  const api = useMemo(() => printQueue(target), [target]);
  const canWrite = usePrintQueueWrite();
  const links = usePrintQueueLinks(target);
  const navigate = useNavigate();
  const [tenant] = useParam('tenant', '');
  const [server] = useParam('server', '');
  const data = useAsync(async () => {
    const [printers, servers, printed] = await Promise.all([
      api.printers({ tenantId: tenant, printServerName: server }),
      printServersOrNull(api, tenant),
      api.jobs({
        tenantId: tenant,
        printServerName: server,
        statuses: ['Printed'],
        size: RECENT_PRINTED * 3,
        sortAsc: false,
      }),
    ]);
    const releases = await releasesOrNull(api);
    return { printers, servers, printed: newestPrinted(printed), releases };
  }, [api, tenant, server]);
  const [removing, setRemoving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [askingLogs, setAskingLogs] = useState(false);
  const [auto, setAuto] = useState(true);
  useInterval(() => void data.reload(), REFRESH_MS, auto);
  const toast = useNotice();
  const listPath = pqPath(`/printqueue/printservers?tenant=${encodeURIComponent(tenant)}`, target);
  const tracked = !!data.data && data.data.servers !== null;
  const g = useMemo<ServerGroup | null>(() => {
    if (!data.data) return null;
    const same = (t: string, n: string) => t === tenant && n.toLowerCase() === server.toLowerCase();
    return (
      serverGroups(
        data.data.printers.filter((p) => same(p.tenantId, p.printServerName)),
        data.data.servers?.filter((s) => same(s.tenantId, s.name)) ?? null
      )[0] ?? null
    );
  }, [data.data, tenant, server]);
  const info = g?.info;

  // While the print server has not answered a logs request: its row again every 5 s, not the whole page.
  useInterval(
    () =>
      void printServersOrNull(api, tenant)
        .then((servers) => data.setData((d) => (d ? { ...d, servers } : d)))
        .catch(() => undefined),
    5000,
    logsWaiting(info)
  );

  const withdrawLogs = async () => {
    try {
      await api.cancelLogs({ tenantId: tenant, name: g?.name ?? server });
      toast.success(
        info?.logsState === 'ready'
          ? `Deleted the logs of ${server}`
          : `Withdrew the logs request to ${server}`
      );
      await data.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const downloadLogs = async () => {
    try {
      const f = await api.downloadLogs(tenant, g?.name ?? server);
      saveBlob(f.blob, f.fileName);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const cancelUpdate = async () => {
    try {
      await api.cancelPrintServerUpdate({ tenantId: tenant, name: g?.name ?? server });
      toast.success(`Withdrew the update of ${server}`);
      await data.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const removePrinter = async (p: Printer) => {
    try {
      await api.deletePrinters([p.id]);
      toast.success(`Deleted printer ${p.name}`);
      await data.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Page
      title={
        <>
          <Link to={listPath} className="nyk-back">
            Print servers
          </Link>
          <span className="nyk-crumb-sep"> / </span>
          {server}
        </>
      }
      description={
        <span className="nyk-inline">
          {tracked && g && <PrintServerStateChip server={info} />}
          <span>{tenantLabel(tenant, links)}</span>
        </span>
      }
      actions={
        <span className="nyk-inline">
          <RefreshControls
            auto={auto}
            onAuto={setAuto}
            loading={data.loading && !!data.data}
            onRefresh={() => void data.reload()}
          />
          {canWrite && g && (
            <>
              {info?.updatable && data.data?.releases?.configured && (
                <Button size="sm" variant="ghost" onClick={() => setUpdating(true)}>
                  Update…
                </Button>
              )}
              {tracked && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canSendLogs(info)}
                  title={canSendLogs(info) ? undefined : `Needs print server ${LOGS_MIN_VERSION} or later`}
                  onClick={() => setAskingLogs(true)}
                >
                  Get logs…
                </Button>
              )}
              {(info?.updateState === 'requested' || info?.updateState === 'sent') && (
                <ConfirmButton
                  size="sm"
                  variant="ghost"
                  message={`Withdraw the update of ${server} to ${info.updateTargetVersion}?`}
                  onConfirm={() => void cancelUpdate()}
                >
                  Withdraw update
                </ConfirmButton>
              )}
              <Button size="sm" variant="ghost" onClick={() => setRemoving(true)}>
                Remove print server
              </Button>
            </>
          )}
        </span>
      }
    >
      {toast.element}
      <ErrorBox error={data.error} />
      <Loading loading={data.loading && !data.data} />
      {data.data && !g && <EmptyState title={`No print server ${server} in tenant ${tenant}.`} />}
      {g && (
        <>
          <section className="nyk-card" data-testid="pq-server-info">
            <dl className="pq-defs">
              <dt>Tenant</dt>
              <dd>
                <TenantCell tenantId={g.tenantId} links={links} target={target} />
              </dd>
              <dt>Jobs</dt>
              <dd>
                <JobLinks g={g} target={target} />
              </dd>
              {tracked ? (
                <>
                  <dt>Last contact</dt>
                  <dd>{info?.lastSeenAt ? when(info.lastSeenAt) : 'not seen yet'}</dd>
                  <Contact label="Hub connected" at={info?.connectedAt} />
                  <Contact label="Hub disconnected" at={info?.disconnectedAt} />
                  <Contact label="Last hub contact" at={info?.lastHubAt} />
                  <Contact label="Last poll" at={info?.lastPollAt} />
                  <Contact label="Printers uploaded" at={info?.lastUploadAt} />
                  <Contact label="Last printed" at={info?.lastPrintedAt} />
                  {info?.version && (
                    <>
                      <dt>Version</dt>
                      <dd>
                        <VersionCell server={info} latest={latestRelease(data.data?.releases ?? null)} />
                      </dd>
                      {info.updateState && (
                        <>
                          <dt>Update</dt>
                          <dd data-testid="pq-update-state">
                            <span className="nyk-inline">
                              <UpdateStateChip server={info} />
                              {info.updateMessage && (
                                <span className="nyk-ui-hint">{info.updateMessage}</span>
                              )}
                            </span>
                          </dd>
                        </>
                      )}
                      <dt>Bootstrapper</dt>
                      <dd>{info.bootstrapperVersion ?? 'not run by the bootstrapper: update it by hand'}</dd>
                    </>
                  )}
                  <dt>Logs</dt>
                  <dd data-testid="pq-logs">
                    <LogsRow
                      server={info ?? null}
                      canWrite={canWrite}
                      viewPath={pqPath(
                        `/printqueue/printserver/logs?tenant=${encodeURIComponent(tenant)}&server=${encodeURIComponent(g?.name ?? server)}`,
                        target
                      )}
                      onDownload={() => void downloadLogs()}
                      onWithdraw={() => void withdrawLogs()}
                    />
                  </dd>
                  {info?.userAgent && (
                    <>
                      <dt>Client</dt>
                      <dd>{info.userAgent}</dd>
                    </>
                  )}
                </>
              ) : (
                <>
                  <dt>State</dt>
                  <dd className="nyk-ui-hint" data-testid="pq-untracked">
                    This print queue is older than 1.5.0: it does not record when print servers were last in
                    contact.
                  </dd>
                </>
              )}
            </dl>
          </section>
          <div className="nyk-card">
            <div className="nyk-card-head">
              <h2>Printers ({g.printers.length})</h2>
            </div>
            {g.printers.length ? (
              <TableScroller>
                <table className="nyk-ui-table nyk-cards" data-testid="pq-printers">
                  <thead>
                    <tr>
                      <th>Printer</th>
                      <th>Added</th>
                      {canWrite && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {g.printers.map((p) => (
                      <tr key={p.id} data-printer={p.id}>
                        <td data-label="Printer">{p.name}</td>
                        <td data-label="Added">{formatDateTime(p.dateAdded)}</td>
                        {canWrite && (
                          <td className="nyk-row-actions">
                            <ConfirmButton
                              size="sm"
                              variant="ghost"
                              message={`Delete printer ${p.name} of ${g.name}? It comes back when the print server reports it again.`}
                              onConfirm={() => void removePrinter(p)}
                            >
                              Delete
                            </ConfirmButton>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            ) : (
              <EmptyState title="No printers reported." />
            )}
          </div>
        </>
      )}
      {g && data.data && (
        <div className="nyk-card">
          <div className="nyk-card-head">
            <h2>Recently printed</h2>
            <Link to={jobsPath(g, target, 'Printed')}>All printed jobs</Link>
          </div>
          {data.data.printed.length ? (
            <TableScroller>
              <table className="nyk-ui-table nyk-cards" data-testid="pq-printed">
                <thead>
                  <tr>
                    <th>Printed</th>
                    <th>Printer</th>
                    <th>File</th>
                    <th>Copies</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.printed.map((j) => (
                    <tr key={j.id} data-job={j.id}>
                      <td data-label="Printed" className="nyk-nowrap">
                        {j.modificationDate ? formatDateTime(j.modificationDate) : '—'}
                      </td>
                      <td data-label="Printer">{j.printerName}</td>
                      <td data-label="File">
                        {j.fileName || <span className="nyk-ui-hint">{j.printType}</span>}
                      </td>
                      <td data-label="Copies">{j.copies}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          ) : (
            <EmptyState title="No printed jobs kept: the print queue deletes them a day after printing." />
          )}
        </div>
      )}
      {updating && g && data.data?.releases && (
        <UpdateDialog
          update={{ tenantId: g.tenantId, name: g.name, label: g.name, version: info?.version }}
          releases={data.data.releases}
          target={target}
          onClose={() => setUpdating(false)}
          onDone={(message, problems) => {
            setUpdating(false);
            announce(toast, message, problems);
            void data.reload();
          }}
        />
      )}
      {askingLogs && g && (
        <LogsDialog
          tenantId={g.tenantId}
          name={g.name}
          replaces={info?.logsState === 'ready'}
          target={target}
          onClose={() => setAskingLogs(false)}
          onDone={(days) => {
            setAskingLogs(false);
            toast.success(`Asked ${g.name} for ${plural(days, 'day')} of logs`);
            void data.reload();
          }}
        />
      )}
      {removing && g && (
        <RemoveDialog
          group={g}
          tracked={tracked}
          target={target}
          onClose={() => setRemoving(false)}
          onRemoved={(message, problems) => {
            setRemoving(false);
            navigate(listPath, { state: { notice: message, problems } satisfies Removed });
          }}
        />
      )}
    </Page>
  );
}

/** The newest printed jobs first: by when they were printed (print queue 1.5.1+), else by id. */
function newestPrinted(jobs: PrintJob[]): PrintJob[] {
  const at = (j: PrintJob) => (j.modificationDate ? Date.parse(j.modificationDate) : 0);
  return [...jobs].sort((a, b) => at(b) - at(a) || b.id - a.id).slice(0, RECENT_PRINTED);
}

function LastPrinted({ server }: { server: PrintServer | null }) {
  if (!server?.lastPrintedAt) return <span className="nyk-ui-hint">—</span>;
  return <span title={formatDateTime(server.lastPrintedAt)}>{formatRelative(server.lastPrintedAt)}</span>;
}

const when = (at: string) => `${formatDateTime(at)} (${formatRelative(at)})`;

function Contact({ label, at }: { label: string; at: string | null | undefined }) {
  if (!at) return null;
  return (
    <>
      <dt>{label}</dt>
      <dd>{when(at)}</dd>
    </>
  );
}

/** The logs request's state with what it holds, Download when ready and Withdraw / Delete (write scope). */
function LogsRow({
  server,
  canWrite,
  viewPath,
  onDownload,
  onWithdraw,
}: {
  server: PrintServer | null;
  canWrite: boolean;
  /** The logs read in the hub (PrintServerLogs page). */
  viewPath: string;
  onDownload: () => void;
  onWithdraw: () => void;
}) {
  const navigate = useNavigate();
  if (!server?.logsState)
    return (
      <span className="nyk-ui-hint">
        {canSendLogs(server)
          ? 'none fetched: Get logs… asks the print server for its log files'
          : `none: a print server sends its logs from ${LOGS_MIN_VERSION} (with print queue 1.12.0)`}
      </span>
    );
  const waiting = logsWaiting(server);
  const days = server.logsDays ? plural(server.logsDays, 'day') : null;
  return (
    <span className="nyk-inline">
      <LogsStateChip server={server} />
      {waiting && (
        <span className="nyk-ui-hint">
          {[days, server.logsRequestedAt && `asked ${formatRelative(server.logsRequestedAt)}`]
            .filter(Boolean)
            .join(', ')}
          {server.state === 'polling' ? ' · a polling print server answers within about 5 minutes' : ''}
        </span>
      )}
      {server.logsState === 'ready' && (
        <span>
          {[
            server.logsSize != null && formatBytes(server.logsSize),
            days,
            server.logsCreatedAt && `uploaded ${when(server.logsCreatedAt)}`,
          ]
            .filter(Boolean)
            .join(', ')}
        </span>
      )}
      {server.logsState === 'failed' && server.logsMessage && (
        <span className="nyk-ui-hint">{server.logsMessage}</span>
      )}
      {canWrite && server.logsState === 'ready' && (
        <>
          <Button size="sm" onClick={() => navigate(viewPath)} data-testid="pq-view-logs">
            View logs
          </Button>
          <Button size="sm" variant="ghost" onClick={onDownload}>
            Download logs
          </Button>
        </>
      )}
      {canWrite && (waiting || server.logsState === 'ready') && (
        <ConfirmButton
          size="sm"
          variant="ghost"
          message={
            waiting
              ? `Withdraw the logs request to ${server.name}?`
              : `Delete the logs of ${server.name} from the print queue?`
          }
          onConfirm={onWithdraw}
        >
          {waiting ? 'Withdraw' : 'Delete logs'}
        </ConfirmButton>
      )}
    </span>
  );
}

/**
 * Asks a print server (3.1.0+) for its recent log files: the print queue relays the request (at once over its hub, or
 * with the next poll) and keeps the zip the print server uploads for 7 days.
 */
function LogsDialog({
  tenantId,
  name,
  replaces,
  target,
  onClose,
  onDone,
}: {
  tenantId: string;
  name: string;
  /** A zip is ready: it is deleted. */
  replaces: boolean;
  target: string;
  onClose: () => void;
  onDone: (days: number) => void;
}) {
  const api = useMemo(() => printQueue(target), [target]);
  const [days, setDays] = useState('2');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const n = Number(days);
  const valid = Number.isInteger(n) && n >= 1 && n <= 14;

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.requestLogs({ tenantId, name, days: n });
      onDone(r.result?.days ?? n);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormDialog
      onClose={onClose}
      title={`Get logs of ${name}`}
      submitLabel="Get logs"
      canSubmit={valid}
      busy={busy}
      error={error}
      onSubmit={() => void send()}
      formClassName="pq-form"
    >
      <TextField
        label="Days (1–14)"
        name="days"
        type="number"
        min={1}
        max={14}
        value={days}
        onChange={(e) => setDays(e.target.value)}
        data-testid="pq-logs-days"
      />
      {replaces && <p className="nyk-text-amber">The logs fetched before are deleted.</p>}
      <p className="nyk-ui-hint">
        A connected print server answers within seconds, a polling one within about 5 minutes; without an
        answer after 30 minutes the request fails. The print queue keeps the zip (up to 20 MB) for 7 days.
      </p>
    </FormDialog>
  );
}

/** The old Printers page: a print server filter opens that print server, otherwise the list. */
export function PrintersRedirect() {
  const { search } = useLocation();
  const q = new URLSearchParams(search);
  const to = q.get('tenant') && q.get('server') ? '/printqueue/printserver' : '/printqueue/printservers';
  if (to === '/printqueue/printservers') q.delete('server');
  return <Navigate to={`${to}?${q.toString()}`} replace />;
}

function RemoveDialog({
  group,
  tracked,
  target,
  onClose,
  onRemoved,
}: {
  group: ServerGroup;
  tracked: boolean;
  target: string;
  onClose: () => void;
  onRemoved: (message: string, problems: string[]) => void;
}) {
  const api = useMemo(() => printQueue(target), [target]);
  const { signedIn } = useTargets();
  const session = signedIn(target);
  const [deleteJobs, setDeleteJobs] = useState(tracked);
  const [revokeTokens, setRevokeTokens] = useState(false);
  const [removeAuthClient, setRemoveAuthClient] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = group.info?.state === 'live' || group.info?.state === 'polling';
  const jobs = (group.info?.openJobs ?? 0) + (group.info?.errorJobs ?? 0);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.removePrintServer({
        tenantId: group.tenantId,
        name: group.name,
        deleteJobs: tracked && deleteJobs,
        revokeTokens,
        removeAuthClient: session && removeAuthClient,
      });
      const parts = [
        plural(r.printers, 'printer'),
        r.jobs ? plural(r.jobs, 'job') : null,
        r.tokensRevoked ? plural(r.tokensRevoked, 'token') : null,
        r.authClient ? `AUTH client ${r.authClient}` : null,
      ].filter(Boolean);
      onRemoved(`Removed ${group.name}: ${parts.join(', ')}`, r.problems);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Remove print server ${group.name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" loading={busy} onClick={() => void remove()}>
            Remove
          </Button>
        </>
      }
    >
      <div className="pq-form" data-testid="pq-remove-server">
        <ErrorBox error={error} />
        <p>
          Deletes the {plural(group.printers.length, 'printer')} of <b>{group.name}</b> ({group.tenantId})
          {tracked ? ' and its contact record' : ''}.
        </p>
        {running && (
          <p className="nyk-text-amber" data-testid="pq-still-running">
            It is still {group.info?.state === 'live' ? 'connected' : 'polling'}: it registers again with its
            next contact or printer upload. Uninstall it first, or revoke its credentials below.
          </p>
        )}
        {tracked ? (
          <Switch
            checked={deleteJobs}
            onChange={setDeleteJobs}
            label={`Delete every job queued for it${jobs ? ` (${jobs} unfinished or failed)` : ''}`}
          />
        ) : (
          <p className="nyk-ui-hint">
            This print queue is older than 1.5.0: only the printers are removed; delete its jobs under Jobs.
          </p>
        )}
        <Switch
          checked={revokeTokens}
          onChange={setRevokeTokens}
          label="Revoke its print queue tokens (PrintServer, same name)"
        />
        <Switch
          checked={session && removeAuthClient}
          onChange={setRemoveAuthClient}
          disabled={!session}
          label={
            session ? 'Remove its AUTH client' : `Remove its AUTH client — sign in to AUTH ${target} first`
          }
        />
      </div>
    </Dialog>
  );
}

/** One print server (`name`) or every one of a tenant. */
interface UpdateTarget {
  tenantId: string;
  name?: string;
  label: string;
  /** The version it runs. */
  version?: string | null;
}

/**
 * Asks print servers (3.0+, run by the bootstrapper) to update: the newest release or a chosen one. Each downloads the
 * package from the print queue, lets running prints finish and restarts into it; the bootstrapper rolls back a version
 * that does not reach the print queue.
 */
function UpdateDialog({
  update,
  releases,
  target,
  onClose,
  onDone,
}: {
  update: UpdateTarget;
  releases: PrintServerReleases;
  target: string;
  onClose: () => void;
  onDone: (message: string, problems: string[]) => void;
}) {
  const api = useMemo(() => printQueue(target), [target]);
  const latest = latestRelease(releases);
  const [version, setVersion] = useState('');
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      if (update.name) {
        const r = await api.updatePrintServer({
          tenantId: update.tenantId,
          name: update.name,
          version: version || undefined,
          force: force || undefined,
        });
        onDone(`Asked ${update.name} to update to ${r.result.version}`, []);
      } else {
        const r = await api.updatePrintServers({
          tenantId: update.tenantId,
          version: version || undefined,
          force: force || undefined,
        });
        const asked = r.result.filter((x) => !x.code);
        const skipped = r.result.filter((x) => x.code && x.code !== 'up_to_date');
        const current = r.result.length - asked.length - skipped.length;
        onDone(
          `Asked ${plural(asked.length, 'print server')} to update` +
            (asked[0]?.version ? ` to ${asked[0].version}` : '') +
            (current ? `; ${current} already up to date` : ''),
          skipped.map((x) => `${x.name}: ${x.error ?? x.code}`)
        );
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Update ${update.label}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void send()}>
            Update
          </Button>
        </>
      }
    >
      <div className="pq-form" data-testid="pq-update-server">
        <ErrorBox error={error} />
        {update.version && (
          <p>
            Runs <b>{update.version}</b>.
          </p>
        )}
        <Select
          aria-label="Version"
          value={version}
          onChange={setVersion}
          options={[
            { value: '', label: latest ? `Newest (${latest})` : 'Newest' },
            ...releases.releases.map((r) => ({
              value: r.version,
              label: `${r.version}${r.prerelease ? ' (prerelease)' : ''}${r.date ? ` · ${formatDateTime(r.date)}` : ''}`,
            })),
          ]}
        />
        <Switch
          checked={force}
          onChange={setForce}
          label="Force: also the version it runs, or one that failed to start there before"
        />
        <p className="nyk-ui-hint">
          Running prints finish first (at most 10 minutes); jobs not started go back to the queue. A version
          that does not reach the print queue within 3 minutes is rolled back. Print servers older than 3.0
          are updated by hand once (install.ps1, docker compose pull).
        </p>
      </div>
    </Dialog>
  );
}

const describePolicy = (p: PrintServerUpdatePolicy) =>
  p.mode === 'Off'
    ? 'Updates by hand only'
    : `Auto-update to ${p.targetVersion === 'latest' ? 'the newest release' : p.targetVersion}` +
      (p.windowStart && p.windowEnd ? ` ${p.windowStart}–${p.windowEnd} (${p.timeZone})` : ' at any time') +
      `, ${p.maxParallel} at a time`;

/**
 * A tenant's update policy: with Auto the print queue keeps its updatable print servers on the newest (or a pinned)
 * release, inside the window, a few at a time; one whose update failed is left alone until updated by hand.
 */
function PolicyDialog({
  tenantId,
  label,
  current,
  releases,
  target,
  onClose,
  onSaved,
}: {
  tenantId: string;
  label: string;
  current: PrintServerUpdatePolicy | null;
  releases: PrintServerReleases;
  target: string;
  onClose: () => void;
  onSaved: (policy: PrintServerUpdatePolicy) => void;
}) {
  const api = useMemo(() => printQueue(target), [target]);
  const [mode, setMode] = useState<'Off' | 'Auto'>(current?.mode ?? 'Off');
  const [version, setVersion] = useState(current?.targetVersion ?? 'latest');
  const [windowStart, setWindowStart] = useState(current?.windowStart ?? '');
  const [windowEnd, setWindowEnd] = useState(current?.windowEnd ?? '');
  const [timeZone, setTimeZone] = useState(current?.timeZone ?? 'Europe/Stockholm');
  const [maxParallel, setMaxParallel] = useState(String(current?.maxParallel ?? 1));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.setUpdatePolicy({
        tenantId,
        mode,
        targetVersion: version,
        windowStart: windowStart.trim() || null,
        windowEnd: windowEnd.trim() || null,
        timeZone: timeZone.trim(),
        maxParallel: Number(maxParallel) || 1,
      });
      onSaved(r.result);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Update policy of ${label}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="pq-form" data-testid="pq-policy">
        <ErrorBox error={error} />
        <Select
          aria-label="Mode"
          value={mode}
          onChange={(v) => setMode(v as 'Off' | 'Auto')}
          options={[
            { value: 'Off', label: 'Off: update by hand' },
            { value: 'Auto', label: 'Auto: keep them on a version' },
          ]}
        />
        <Select
          aria-label="Target version"
          value={version}
          onChange={setVersion}
          options={[
            {
              value: 'latest',
              label: `Newest release${latestRelease(releases) ? ` (now ${latestRelease(releases)})` : ''}`,
            },
            ...releases.releases.map((r) => ({
              value: r.version,
              label: `${r.version}${r.prerelease ? ' (prerelease)' : ''}`,
            })),
          ]}
        />
        <TextField
          label="Window from (HH:mm)"
          value={windowStart}
          onChange={(e) => setWindowStart(e.target.value)}
        />
        <TextField
          label="Window to (HH:mm)"
          value={windowEnd}
          onChange={(e) => setWindowEnd(e.target.value)}
        />
        <TextField label="Time zone" value={timeZone} onChange={(e) => setTimeZone(e.target.value)} />
        <TextField
          label="Updating at once"
          type="number"
          min={1}
          value={maxParallel}
          onChange={(e) => setMaxParallel(e.target.value)}
        />
        <p className="nyk-ui-hint">
          No window: at any time. A window may cross midnight (22:00–05:00). Offline print servers are updated
          when they are back; one whose update failed is left alone until it is updated by hand.
        </p>
      </div>
    </Dialog>
  );
}
